import { Button, Input, Label, Select, Spinner } from '@crm/ui';
import { useMutation } from '@tanstack/react-query';
import { Check, MessageCircle, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { useCreateCustomer, useCustomers } from '@/modules/customers/api';
import { type Gender, landingService } from '@/modules/landing/api';
import { useBookingMode } from '../../booking-mode';

const GENDERS: { value: Gender; label: string }[] = [
  { value: 'FEMALE', label: 'Feminino' },
  { value: 'MALE', label: 'Masculino' },
  { value: 'UNDISCLOSED', label: 'Prefiro nao informar' },
];

const currentYear = new Date().getFullYear();
const onlyDigits = (value: string) => value.replace(/\D/g, '');

export interface IdentifiedCustomer {
  id: string;
  name: string;
  phone: string | null;
  /** Prova de posse do telefone; so existe no agendamento publico. */
  token?: string;
}

interface CustomerIdentifyProps {
  onIdentified: (customer: IdentifiedCustomer) => void;
  onCancel?: () => void;
}

/**
 * Identificacao pelo WhatsApp: o numero diz se a pessoa ja tem cadastro. Se
 * tem, ela so confirma; se nao, completa o cadastro ali mesmo.
 *
 * Na pagina publica os dados voltam mascarados do servidor - o suficiente para
 * se reconhecer, nada que sirva para descobrir o cadastro de outra pessoa.
 * Dentro do sistema quem opera ja tem acesso ao cadastro, entao o nome aparece
 * inteiro.
 */
export function CustomerIdentify({ onIdentified, onCancel }: CustomerIdentifyProps) {
  const mode = useBookingMode();
  const [phone, setPhone] = useState('');
  const [found, setFound] = useState<{
    name: string;
    phone: string;
    token?: string;
    id?: string;
  } | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender>('UNDISCLOSED');
  const [birthYear, setBirthYear] = useState('');

  // Dentro do sistema a procura usa a propria busca de clientes.
  const internalSearch = useCustomers(
    { page: 1, search: mode ? undefined : onlyDigits(phone), status: 'active' },
    { enabled: mode === null },
  );
  const createCustomer = useCreateCustomer();

  const lookup = useMutation({
    mutationFn: async () => {
      if (mode) return landingService.lookup(mode.slug, phone);

      // Mesma regra do servidor: compara a cauda, porque o cadastro costuma
      // ter DDI e quem digita manda so DDD e numero.
      const digits = onlyDigits(phone).slice(-11);
      const match = (await internalSearch.refetch()).data?.data.find(
        (customer) => onlyDigits(customer.phone ?? '').slice(-11) === digits,
      );

      return match
        ? {
            found: true,
            maskedName: match.name,
            maskedPhone: match.phone,
            token: null,
            id: match.id,
          }
        : { found: false, maskedName: null, maskedPhone: null, token: null };
    },
    onSuccess: (result) => {
      if (result.found) {
        setFound({
          name: result.maskedName ?? '',
          phone: result.maskedPhone ?? phone,
          token: result.token ?? undefined,
          id: 'id' in result ? (result.id as string) : undefined,
        });
        setIsNew(false);
        return;
      }

      setFound(null);
      setIsNew(true);
    },
  });

  const register = useMutation({
    mutationFn: async (): Promise<IdentifiedCustomer> => {
      if (mode) {
        const created = await landingService.register({
          slug: mode.slug,
          phone,
          name,
          gender,
          birthYear: Number(birthYear),
        });

        return {
          id: '',
          name: created.maskedName,
          phone: created.maskedPhone,
          token: created.token,
        };
      }

      const created = await createCustomer.mutateAsync({
        name,
        phone,
        gender,
        birthYear: Number(birthYear),
        isFavorite: false,
        isActive: true,
      });

      return { id: created.id, name: created.name, phone: created.phone };
    },
    onSuccess: onIdentified,
  });

  const error = lookup.error ?? register.error;
  const canRegister = name.trim().length >= 2 && Number(birthYear) >= 1900;

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="identify-phone">WhatsApp</Label>
        <div className="flex gap-2">
          <Input
            id="identify-phone"
            type="tel"
            autoComplete="tel"
            placeholder="(11) 90000-0000"
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              setFound(null);
              setIsNew(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && onlyDigits(phone).length >= 10) lookup.mutate();
            }}
          />
          <Button
            className="shrink-0"
            disabled={onlyDigits(phone).length < 10 || lookup.isPending}
            onClick={() => lookup.mutate()}
          >
            {lookup.isPending ? <Spinner /> : <MessageCircle />}
            Confirmar
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          E por ele que a confirmacao do horario e enviada.
        </p>
      </div>

      {error ? (
        <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-base text-destructive">
          {toErrorMessage(error)}
        </p>
      ) : null}

      {found ? (
        <div className="space-y-4 rounded-xl border border-border p-4">
          <div>
            <p className="text-sm text-muted-foreground">Encontramos este cadastro:</p>
            <p className="mt-1 text-lg font-semibold">{found.name}</p>
            <p className="text-sm text-muted-foreground">{found.phone}</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="flex-1"
              onClick={() =>
                onIdentified({
                  id: found.id ?? '',
                  name: found.name,
                  phone: found.phone,
                  token: found.token,
                })
              }
            >
              <Check />
              Sou eu, continuar
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setFound(null);
                setIsNew(true);
              }}
            >
              <UserPlus />
              Nao sou eu
            </Button>
          </div>
        </div>
      ) : null}

      {isNew ? (
        <div className="space-y-4 rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">
            Primeiro atendimento por aqui. Complete o cadastro:
          </p>

          <div className="space-y-2">
            <Label htmlFor="identify-name">Nome</Label>
            <Input
              id="identify-name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="identify-gender">Genero</Label>
            <Select
              id="identify-gender"
              value={gender}
              onChange={(event) => setGender(event.target.value as Gender)}
            >
              {GENDERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="identify-year">Ano de nascimento</Label>
            <Select
              id="identify-year"
              value={birthYear}
              onChange={(event) => setBirthYear(event.target.value)}
            >
              <option value="">Selecione</option>
              {Array.from({ length: 90 }, (_, index) => currentYear - 12 - index).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </div>

          <Button
            className="w-full"
            disabled={!canRegister || register.isPending}
            onClick={() => register.mutate()}
          >
            {register.isPending ? <Spinner /> : <Check />}
            Confirmar cadastro
          </Button>
        </div>
      ) : null}

      {onCancel ? (
        <Button variant="ghost" className="w-full" onClick={onCancel}>
          Voltar para a busca
        </Button>
      ) : null}
    </div>
  );
}
